import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ВЫБОР КОШЕЛЬКА — ОДИН НА ДВА ОКНА.
 *
 * Кошелёк выбирают в двух местах: «Пополнить счёт кампании» (кладут
 * сейчас, руками) и «Автопополнение счёта» (кладёт сервис сам). Пока
 * это были две разные разметки, они и выглядели по-разному: в ручном
 * окне карточки с суммами, в автопополнении — голые кнопки-таблетки
 * без единого числа. Владелец 09.09.2026: «в автопополнении сделаем
 * кнопки с отображением как в пополнении ручным способом, с
 * отображением сумм и баланса».
 *
 * Две копии одного выбора — это два места, где надо вспомнить про
 * пустой кошелёк, про неизвестный остаток и про то, что деньги берут
 * отсюда. Здесь одно.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЭТО ОТХОД ОТ ЭТАЛОНА, И ОТХОД НАРОЧНЫЙ.
 *
 * В эталоне у окна автопополнения кошельки выбираются таблетками
 * `height: 30px` — теми же, что «Остановить показы / Только
 * предупредить» в окне лимита. Карточки с суммами эталон рисует
 * только в ручном пополнении. Здесь эталон переигран решением
 * владельца от 09.09.2026, потому что вопрос у двух окон один
 * и тот же — «хватит ли там денег», — а ответ был только в одном.
 * Придёт новый эталон — эту разницу видно в стороже, а не в чьей-то
 * памяти.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * БОНУСОВ ЗДЕСЬ НЕТ, И ЭТО РЕШЕНИЕ ВЛАДЕЛЬЦА НА ЗАМЕРЕ.
 *
 * «Давай тогда уберём бонусы вообще» (09.09.2026), и замер это
 * подтверждает: остатка бонусов не отдаёт ни один метод рекламного API.
 * Восемь путей проверены живьём — четыре 404, `/adv/v1/payments` пуст
 * за весь год, а `/adv/v1/balance` отвечает
 * `{"balance":0,"net":84641,"currency":"RUB"}`.
 *
 * Ноль у счёта в этом ответе стоит ЯВНО — значит пустые кошельки
 * Wildberries не опускает, и молчание про `bonus` означает «такого
 * кошелька в ответе нет». Кнопка, которая никогда не покажет суммы,
 * — это выбор вслепую: нажал, а там пусто, и Wildberries отказал
 * с непонятной причиной. За год в `upd` бонусами не оплачено ни разу:
 * 12 503 списания «Баланс», 4 — «Счет».
 *
 * СЕРВЕР `bonus` ПО-ПРЕЖНЕМУ ПРИНИМАЕТ, и это нарочно: у кого-то
 * настройка могла быть сохранена бонусами, и стереть её вместе
 * с кнопкой значило бы молча переставить чужие деньги на другой
 * кошелёк. Пока человек не сохранит форму заново, сохранённое живёт,
 * а окно про это ГОВОРИТ — см. `data-d="кошелёк-снят"` ниже.
 * ═══════════════════════════════════════════════════════════════════════
 */

/** Источник денег. `bonus` остался в типе — см. шапку. */
export type Source = 'account' | 'balance' | 'bonus';

/**
 * Кошельки, которые ПОКАЗЫВАЮТСЯ.
 *
 * ДВА НАЗВАНИЯ У КАЖДОГО, И ЭТО НЕ ИЗБЫТОЧНОСТЬ. `title` — ярлык
 * на карточке («Счёт»); `from` — то же самое внутри фразы («Положить
 * 1 000 ₽ со счёта»), где без предлога выходит косноязычие.
 */
export const SOURCES: Array<{ value: Source; title: string; from: string }> = [
  { value: 'account', title: 'Счёт', from: 'со счёта' },
  { value: 'balance', title: 'Баланс', from: 'с баланса' },
];

/** Слово для фразы — по любому источнику, включая снятый с показа. */
export const ОТКУДА: Readonly<Record<Source, string>> = {
  account: 'со счёта',
  balance: 'с баланса',
  bonus: 'с бонусов',
};

/** Этот кошелёк с показа снят, а в сохранённой настройке ещё стоит. */
export function снятСПоказа(s: Source): boolean {
  return !SOURCES.some((x) => x.value === s);
}

export interface Кошельки {
  /**
   * `null` — Wildberries про этот кошелёк не сказал ничего.
   *
   * НЕ ТО ЖЕ САМОЕ, ЧТО НОЛЬ. Ноль гасит карточку, `null` оставляет
   * её без суммы и доступной: мы не знаем, пусто там или нет.
   */
  account: number | null;
  balance: number | null;
  bonus: number | null;
}

const money = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

/**
 * Спросить кошельки кабинета.
 *
 * СПРАШИВАЕМ КАЖДЫЙ РАЗ, А НЕ ХРАНИМ. Кошельки общие на кабинет
 * и меняются от каждой траты любой кампании; вчерашнее число здесь
 * хуже отсутствующего — по нему решают о деньгах прямо сейчас.
 *
 * Ключ запроса общий на оба окна: react-query сам не пошлёт второй
 * запрос, если первый ещё свеж.
 *
 * `нужны` — НЕ УКРАШЕНИЕ, А ЭКОНОМИЯ ЧУЖОГО ПРЕДЕЛА. За этим адресом
 * стоит живой запрос к Wildberries под общим пределом `balance`; форма
 * автопополнения рисуется не только окном, но и карточкой на вкладке,
 * где сумм нет вовсе. Спрашивать кошельки при каждом открытии карточки
 * значило бы тратить предел на числа, которых никто не увидит.
 */
export function useКошельки(base: string, нужны = true): Кошельки | null {
  const ответ = useQuery({
    queryKey: ['wallets', base],
    queryFn: () => api<{ wallets: Кошельки | null }>(`${base}/wallets`),
    enabled: нужны,
    /* ОТКАЗ НЕ ПОВТОРЯЕМ: у наблюдателя прав на этот запрос нет,
       и три попытки подряд ничего не изменят, кроме задержки окна. */
    retry: false,
    staleTime: 30_000,
  });
  return ответ.data?.wallets ?? null;
}

/**
 * Первый кошелёк, откуда точно есть что взять.
 *
 * НЕИЗВЕСТНЫЙ НЕ СЧИТАЕТСЯ НЕПУСТЫМ: про него не сказано, что там
 * есть деньги, — сказано, что мы не знаем. Порядок обхода тот же,
 * что на экране.
 */
export function первыйНепустой(к: Кошельки | null): Source | null {
  if (к === null) return null;
  for (const s of SOURCES) {
    const сумма = к[s.value];
    if (сумма !== null && сумма > 0) return s.value;
  }
  return null;
}

/**
 * Карточки кошельков.
 *
 * Величины — из эталона ручного пополнения (`data-d="кошельки-
 * пополнения"`): карточка `flex: 1 1 128px`, имя мелким, сумма крупнее
 * имени и жирная. Так и выбирают: по сумме, а не по названию.
 */
export function ВыборКошелька({
  кошельки,
  выбран,
  onВыбор,
}: {
  кошельки: Кошельки | null;
  выбран: Source;
  onВыбор: (s: Source) => void;
}) {
  return (
    <>
      <div className="k-выбор-настройки" data-d="кошельки-пополнения">
        {SOURCES.map((s) => {
          const сумма = кошельки === null ? null : кошельки[s.value];
          /* ПУСТОЙ КОШЕЛЁК НЕ ВЫБИРАЕТСЯ. Ноль — это не «мало»,
             а «нечего взять»: пополнение оттуда не пройдёт никогда,
             и кнопка, гарантированно приводящая к отказу, хуже
             отсутствующей. Пока кошельки не узнаны — доступны все. */
          const пуст = сумма !== null && сумма <= 0;
          const этот = s.value === выбран;
          return (
            <button
              key={s.value}
              type="button"
              data-d={`кошелёк-${s.value}`}
              className={`k-кошелёк${этот ? ' k-кошелёк--выбран' : ''}`}
              aria-pressed={этот}
              disabled={пуст}
              title={
                пуст
                  ? `На «${s.title}» пусто — Wildberries не даст взять отсюда`
                  : сумма !== null
                    ? `${s.title}: ${money.format(сумма)} ₽`
                    : /* ═══════════════════════════════════════════
                         ДВА РАЗНЫХ «НЕ ЗНАЕМ», И ПУТАТЬ ИХ НЕЛЬЗЯ.

                         Ответ не пришёл вовсе — Wildberries молчит,
                         и через минуту число может появиться.

                         Ответ пришёл, а этого кошелька в нём нет —
                         число не появится никогда.
                         ═══════════════════════════════════════ */
                      кошельки === null
                      ? 'Сколько лежит на этом кошельке, Wildberries сейчас не ответил'
                      : `Остаток «${s.title}» Wildberries не сообщает — взять отсюда можно, но сколько там, он не говорит`
              }
              onClick={() => onВыбор(s.value)}
            >
              <span className="k-имя-кошелька">{s.title}</span>
              {/* СУММА ВТОРОЙ СТРОКОЙ, КАК В ЭТАЛОНЕ. Пока ответа нет —
                  строки нет вовсе: пустое место честнее прочерка,
                  который читается как «ноль». */}
              {сумма !== null && (
                <b data-d="сумма-кошелька" className="k-сумма-кошелька">
                  {money.format(сумма)} ₽
                </b>
              )}
            </button>
          );
        })}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          СНЯТЫЙ КОШЕЛЁК НЕ МОЛЧИТ.

          Бонусы убраны с показа, но у кого-то настройка сохранена
          именно ими, и сервер её принимает. Без этой строки такой
          человек видел бы выбор, в котором НЕ ВЫДЕЛЕНА НИ ОДНА
          карточка, и не мог бы узнать, откуда сервис берёт деньги.

          Молча переставить его на счёт было бы хуже всего: это
          распоряжение чужими деньгами без спроса. Поэтому настройка
          остаётся как есть, а окно называет её словами — и говорит,
          чем её сменить. У всех прочих строки нет вовсе.
          ═══════════════════════════════════════════════════════ */}
      {снятСПоказа(выбран) && (
        <span className="k-подпись-настройки" data-d="кошелёк-снят">
          Сейчас настроено пополнение {ОТКУДА[выбран]} — этот кошелёк убран, потому что
          Wildberries не сообщает его остаток. Выберите счёт или баланс и сохраните.
        </span>
      )}
    </>
  );
}
